import { useId, useState } from "react";
import { StarRating } from "./StarRating";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { REVIEW_TEXT_MIN, REVIEW_TEXT_MAX } from "@/lib/review-limits";
import { cn } from "@/lib/utils";
import { CircleNotch, WarningCircle } from "@phosphor-icons/react";

type Props = {
  therapistId: string;
  onSuccess?: () => void;
  className?: string;
  initialRating?: number;
  initialText?: string;
  initialAnonymous?: boolean;
  isEdit?: boolean;
};

export function ReviewForm({
  therapistId,
  onSuccess,
  className,
  initialRating = 0,
  initialText = "",
  initialAnonymous = false,
  isEdit = false,
}: Props) {
  const textId = useId();
  const anonId = useId();
  const [rating, setRating] = useState(initialRating);
  const [text, setText] = useState(initialText);
  const [anon, setAnon] = useState(initialAnonymous);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (rating < 1) {
      setError("Odaberite ocjenu.");
      return;
    }
    if (text.trim().length < REVIEW_TEXT_MIN) {
      setError(`Recenzija mora imati barem ${REVIEW_TEXT_MIN} znakova.`);
      return;
    }
    setSubmitting(true);
    const res = await fetch("/api/reviews", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ therapistId, rating, reviewText: text.trim(), isAnonymous: anon }),
    });
    setSubmitting(false);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      setError(body.error ?? "Greška pri slanju.");
      return;
    }
    onSuccess?.();
  }

  return (
    <form onSubmit={submit} className={cn("flex flex-col gap-5", className)}>
      <div className="flex flex-col gap-2">
        <Label>Ocjena</Label>
        <StarRating value={rating} onChange={setRating} size="lg" />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={textId}>Vaša recenzija</Label>
        <Textarea
          id={textId}
          className="min-h-36 resize-none text-sm leading-relaxed"
          rows={5}
          maxLength={REVIEW_TEXT_MAX}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="text-xs text-muted-foreground">
          {text.length}/{REVIEW_TEXT_MAX} (min. {REVIEW_TEXT_MIN})
        </div>
      </div>
      <Label htmlFor={anonId} className="w-fit cursor-pointer text-sm font-normal">
        <Checkbox
          id={anonId}
          checked={anon}
          onCheckedChange={(checked) => setAnon(checked === true)}
        />
        Objavi anonimno
      </Label>
      {error ? (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
          <WarningCircle weight="fill" className="mt-0.5 size-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}
      <Button type="submit" disabled={submitting} size="lg" className="w-full sm:w-fit">
        {submitting ? <CircleNotch className="size-4 animate-spin" /> : null}
        {submitting ? "Slanje…" : isEdit ? "Pošalji ponovo" : "Pošalji recenziju"}
      </Button>
    </form>
  );
}
